import React, { useState, useMemo } from "react";
import { navigate } from "raviger";
import Icon from "../../common/Icon";

const PAGE_SIZE = 5;

interface MonthlyScore {
  month: string;
  year: string;
  overall_score: string | null;
  critical_score: string | null;
  audit_id?: string | null;
}

interface VendorRow {
  vendor_id: string;
  vendor_name: string;
  vendor_category_id: string;
  vendor_category_name: string;
  monthly_scores: MonthlyScore[];
}

interface VendorPerformanceMatrixProps {
  data: VendorRow[] | null;
  isLoading?: boolean;
}

function parseScore(score: string | null | undefined): number | null {
  if (score === null || score === undefined || score === "") return null;
  const num = parseFloat(score.replace("%", ""));
  return isNaN(num) ? null : num;
}

function getCellClass(score: number | null, isCurrent?: boolean): string {
  if (score === null || score === undefined) return "cell-empty";
  if (score < 75) return "cell-danger";
  if (score < 90) return "cell-warning";
  return "cell-success";
}

const VendorPerformanceMatrix: React.FC<VendorPerformanceMatrixProps> = ({
  data,
  isLoading,
}) => {
  const [scoreType, setScoreType] = useState<"overall" | "critical">("overall");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const rows: VendorRow[] = Array.isArray(data) ? data : [];
  const visibleRows = rows.slice(0, visibleCount);
  const hasMore = visibleCount < rows.length;

  const months = useMemo(() => {
    if (rows.length === 0 || !rows[0].monthly_scores) return [];
    return rows[0].monthly_scores.map(
      (ms) => `${ms.month.substring(0, 3)}, ${ms.year.substring(2)}`
    );
  }, [rows]);

  const now = new Date();
  const currentMonthName = now.toLocaleString("en-US", { month: "long" });
  const currentYear = String(now.getFullYear());

  return (
    <div className="section-card">
      <div className="matrix-header">
        <div>
          <div className="section-title">Vendor Performance Matrix</div>
          <div className="section-subtitle">
            Month-wise vendor compliance performance tracking
          </div>
        </div>

        {rows.length > 0 && (
          <>
            <div className="matrix-toggle">
              <div
                className={`toggle-slider ${scoreType === "critical" ? "slider-right" : "slider-left"}`}
                style={{ transform: scoreType === "critical" ? "translateX(100%)" : "translateX(0)" }}
              />
              <button
                className={`toggle-btn ${scoreType === "overall" ? "active" : ""}`}
                onClick={() => setScoreType("overall")}
              >
                Overall Score
              </button>
              <button
                className={`toggle-btn ${scoreType === "critical" ? "active" : ""}`}
                onClick={() => setScoreType("critical")}
              >
                Critical Score
              </button>
            </div>

            <div className="matrix-legend">
              <div className="legend-item">
                <div className="legend-swatch danger" />
                <span className="legend-label">Below 75%</span>
              </div>
              <div className="legend-item">
                <div className="legend-swatch warning" />
                <span className="legend-label">75-89%</span>
              </div>
              <div className="legend-item">
                <div className="legend-swatch success" />
                <span className="legend-label">90-100%</span>
              </div>
            </div>
          </>
        )}
      </div>

      {isLoading ? (
        <div className="section-loading">
          <div className="spinner-border text-secondary" role="status" />
          Loading...
        </div>
      ) : (
        <div className="heatmap-container">
          {rows.length === 0 ? (
            <div className="empty-state-message">
              The final audit report has not yet been published.
            </div>
          ) : (
            <>
              <div className="heatmap-scroll-wrapper">
                <table className="heatmap-table">
                  <thead>
                    <tr>
                      <th className="col-vendor col-sticky">Vendor</th>
                      <th className="col-category col-sticky-2">Category</th>
                      {months.map((month, i) => (
                        <th key={i} className="col-month">
                          {month}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        <td className="td-vendor col-sticky">{row.vendor_name}</td>
                        <td className="td-category col-sticky-2">{row.vendor_category_name}</td>
                        {(row.monthly_scores || []).map((ms, colIdx) => {
                          const rawScore = scoreType === "overall" ? ms.overall_score : ms.critical_score;
                          const score = parseScore(rawScore);
                          const isCurrent = ms.month === currentMonthName && ms.year === currentYear;
                          const hasAudit = !!ms.audit_id;
                          return (
                            <td key={colIdx}>
                              <div
                                className={`heatmap-cell ${getCellClass(score, isCurrent)}${hasAudit ? " cell-clickable" : ""}`}
                                onClick={() => {
                                  if (hasAudit) {
                                    navigate(`/vendor-audits/${ms.audit_id}`);
                                  }
                                }}
                              >
                                <Icon
                                  name="arrowUpRight"
                                  style={{ position: "absolute", top: "-4px", right: "1px"}}
                                />
                                {score !== null ? `${score}%` : "-"}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {hasMore && (
                <div className="heatmap-view-more">
                  <button
                    className="view-more-btn"
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  >
                    View More
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default VendorPerformanceMatrix;
